import { useSession } from '@/context/SessionProvider'
import { Flex, Skeleton, SimpleGrid, Box, Text, useMediaQuery } from '@chakra-ui/react'
import { useQuery } from '@tanstack/react-query'
import { IoMdCloseCircleOutline } from 'react-icons/io'
import FolderIcon from '@/public/assets/icons/folder.svg'
import Link from 'next/link'
import { LootboxLimitedDeprecated } from '../templates/transformLootboxRewards'
import Image from 'next/image'
import { useRouter } from 'next/router'
import { BigNumberInWei } from '@injectivelabs/utils'
import { LastWinsWidget } from './LastWinsWidget/LastWinsWidget'
import { useLootboxContext } from '@/context/LootboxProvider'
import { useTranslation } from 'next-i18next/pages'

export const LootboxWidgets = ({
  polling = false,
  mobile = { slider: true, lastDrop: true },
}: {
  polling?: boolean
  mobile?: { slider: boolean; lastDrop: boolean }
}) => {
  const { account } = useSession()

  return (
    <Flex gap={{ base: 6, sm: 3, lg: 6 }} flexDir={{ base: 'column', md: 'row' }}>
      <Flex
        display={{ base: mobile.slider && mobile.lastDrop ? 'flex' : 'none', sm: 'flex' }}
        gap={{ base: 6, sm: 3, lg: 6 }}
        flexBasis={'full'}
        flexDir={{ base: 'column', sm: 'row' }}
      >
        <Box flexBasis={'full'} display={{ base: mobile.lastDrop ? 'block' : 'none', sm: 'block' }}>
          <LastWinsWidget polling={polling} />
        </Box>
      </Flex>
      {account && <InventoryWidget address={account.injectiveAddress} />}
    </Flex>
  )
}

const InventoryWidget = ({ address }: { address: string }) => {
  const { t } = useTranslation()

  return (
    <Box flexShrink={0} w={'full'} maxW={{ base: 'none', md: '230px', xl: '300px' }}>
      <Flex mb={3} fontWeight={700} justifyContent={'space-between'}>
        <Text>{t('lootbox.your-inventory')}</Text>
        <Link href={'/lootbox/inventory'}>
          <Text
            cursor={'pointer'}
            fontWeight={700}
            color={'brand.indygo'}
            bgBlendMode={'overlay'}
            bg={`linear-gradient(291.97deg, #8E83FB 31.11%, #5D52EE 97.01%), rgba(255, 255, 255, 0.2)`}
            backgroundClip={'text'}
            __css={{
              '-webkit-text-fill-color': 'transparent',
            }}
            _hover={{
              opacity: 0.8,
            }}
          >
            {t('common.see-all')}
          </Text>
        </Link>
      </Flex>
      <InventoryLootboxes address={address} />
    </Box>
  )
}

const InventoryLootboxes = ({ address }: { address: string }) => {
  const { t } = useTranslation()
  const { getLootboxBalances } = useLootboxContext()
  const [isMobile] = useMediaQuery('(max-width: 479px)')

  const { data, isLoading, isError } = useQuery({
    queryKey: ['LOOTBOX_BALANCES', [address]],
    queryFn: async () => {
      return await getLootboxBalances(address)
    },
  })

  if (isError) {
    return (
      <Box p={3} bg={'ninja.700'} rounded={'2xl'}>
        <Flex
          h={{ base: '59px', sm: '118px' }}
          gap={1}
          justifyContent={'center'}
          flexDir={'column'}
          alignItems={'center'}
        >
          <Box color="ninja.400">
            <IoMdCloseCircleOutline size={40} />
          </Box>
          <Text textAlign={'center'} fontWeight={700} color={'ninja.400'}>
            {t('messages.error-inventory')}
          </Text>
        </Flex>
      </Box>
    )
  }

  if (isLoading) {
    return <Skeleton h={{ base: '83px', sm: '142px' }} startColor="ninja.500" endColor="ninja.600" rounded={'2xl'} />
  }

  return (
    <Box p={3} bg={'ninja.700'} rounded={'2xl'}>
      {data?.length ? (
        <SimpleGrid h={{ base: '59px', sm: '118px' }} gap={1} columns={{ base: 4, md: 3, xl: 4 }}>
          {data.slice(0, isMobile ? 3 : 5).map((lootbox: any) => (
            <LootboxInventoryCell key={lootbox.contract} lootbox={lootbox} />
          ))}
          {data.length > (isMobile ? 3 : 5) && <LootboxInventoryMore amount={data.length - 7} />}
        </SimpleGrid>
      ) : (
        <Flex h={'118px'} gap={1} justifyContent={'center'} flexDir={'column'} alignItems={'center'}>
          <FolderIcon />
          <Text fontWeight={700} color={'ninja.400'}>
            {t('lootbox.inventory-empty')}
          </Text>
        </Flex>
      )}
    </Box>
  )
}

const LootboxInventoryMore = ({ amount }: { amount: number }) => {
  return (
    <Flex
      h={14}
      fontWeight={700}
      fontSize={'xs'}
      _hover={{
        bg: 'ninja.600',
      }}
      cursor={'pointer'}
      transition={'.1s'}
      rounded={'xl'}
      justifyContent={'center'}
      align={'center'}
      p={2}
      border={'1px solid'}
      borderColor={'whiteAlpha.300'}
    >
      {amount}
    </Flex>
  )
}

const LootboxInventoryCell = ({ lootbox }: { lootbox: LootboxLimitedDeprecated & { balance: string } }) => {
  const { getLootboxImagePrefix } = useLootboxContext()
  const router = useRouter()

  return (
    <Flex
      h={14}
      onClick={() => {
        router.push('/lootbox/' + lootbox.contract)
      }}
      cursor={'pointer'}
      bg={'ninja.600'}
      rounded={'xl'}
      justifyContent={'center'}
      align={'center'}
      p={2}
      pos={'relative'}
      overflow={'hidden'}
      boxShadow={`0px 0px 16px 0px ${lootbox.image.shadowColor} inset`}
    >
      <Box>
        <Image
          width={42}
          height={40}
          src={getLootboxImagePrefix(lootbox.contract) + (lootbox.image.src || 'lootbox.png')}
          alt=""
        />
      </Box>
      <Box
        fontWeight={700}
        bg={'white'}
        px={1}
        color={'ninja.500'}
        pos={'absolute'}
        bottom={0}
        right={0}
        fontSize={'xs'}
        rounded={'lg'}
      >
        {new BigNumberInWei(lootbox.balance).gt(99) ? '99+' : lootbox.balance}
      </Box>
    </Flex>
  )
}
